import { FC, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import Bakkenteller from '@/components/bakkenteller/Bakkenteller';
import { MwdContextMenuInformation, WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { AccepteerConflictDialog } from '@/components/dialogs/accepteerConflict/AccepteerConflictDialog';
import ClaimDialog, { ClaimDialogData } from '@/components/dialogs/claims/ClaimDialog';
import VerwijderClaimDialog from '@/components/dialogs/claims/VerwijderenClaimDialog';
import DefectGereedMeldenDialog from '@/components/dialogs/defecten/gereedMelden/DefectGereedMeldenDialog';
import MeldDefectDialog from '@/components/dialogs/defecten/meldDefect/MeldDefectDialog';
import ToevoegenInregelenDialog from '@/components/dialogs/inregelen/InregelenDialog';
import VerwijderenInregelenDialog from '@/components/dialogs/inregelen/InregelingVerwijderenDialog';
import KnippenDialog from '@/components/dialogs/knippen/KnippenDialog';
import MultiVersterkenDialog from '@/components/dialogs/versterken/MultiVersterkenDialog';
import { VersterkDialogData, VersterkenDialog } from '@/components/dialogs/versterken/VersterkenDialog';
import MultiVerzwakkenDialog from '@/components/dialogs/verzwakken/MultiVerzwakkenDialog';
import VerzwakDialog from '@/components/dialogs/verzwakken/VerzwakDialog';
import { InzetWijzigenDialog } from '@/components/dialogs/wijzigen/InzetWijzigenDialog';
import { useGetQuery } from '@/queries/useGetQuery';
import useClientState from '@/state/useClientState';
import { identity } from '@/utils/functions';

import useDialogStore from '../../state/selectors/useDialogStore';

/**
 * Lightweight gate component — only subscribes to `showDialog`.
 * When no dialog is open (99% of the time), no heavy dialog hooks run.
 * The dienstregelpunten fetch stays here so it pre-loads on app start.
 */
const MwdActions: FC = () => {
  const showDialog = useClientState((state) => state.actionDialog.showDialog);

  // fetch dienstregelpunten on app start — must stay outside the gate
  // so data is ready before any dialog opens (used by ClaimDialog)
  const { data: dienstregelpunten, fetchData: fetchAlleDrglptnamen } = useGetQuery(identity, true);
  useEffect(() => {
    fetchAlleDrglptnamen('/stamgegevens/drglptnamen').then(() => {});
  }, []);

  if (!showDialog) return null;

  return <MwdActionsContent dienstregelpunten={dienstregelpunten} />;
};

/**
 * Heavy content — only mounted when a dialog is actually open.
 */
const MwdActionsContent: FC<{ dienstregelpunten: string[] }> = ({ dienstregelpunten }) => {
  const { showDialog, dialogKey, dialogData, dialogActionOptions } = useDialogStore();
  const selectedItems = useClientState(useShallow((state) => state.selected.selectedItems));
  const closeDialog = useClientState((state) => state.closeDialog);

  useEffect(() => {
    if (dialogKey === 'materieel-details') {
      const state = useClientState.getState();
      const selectedWerklijnId = state.selected.werklijnen[0];
      const selectedEenheid = state.werklijnen[selectedWerklijnId];
      state.clearInzetPanel();
      state.setShowMaterieelPanel();
      if (state.setMaterieelPanelData && selectedEenheid) {
        state.setMaterieelPanelData(selectedEenheid.eenheid);
      }
      void state.closeDialog();
    }
  }, [dialogKey]);

  return (
    <>
      {showDialog && (
        <>
          {dialogKey === 'bakkenteller' && (
            <Bakkenteller showBakkenteller={showDialog} handleOpenChange={closeDialog} />
          )}
          {['contextmenu', 'sneltoets', 'sidepanel'].includes(dialogActionOptions) && (
            <>
              {dialogKey === 'inregelen' &&
              (dialogData as unknown as MwdContextMenuInformation)?.details !== undefined ? (
                <ToevoegenInregelenDialog
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={
                    (dialogData as unknown as MwdContextMenuInformation)
                      ?.details as unknown as WerklijnContextMenuDetails
                  }
                />
              ) : null}
              {dialogKey === 'versterken' && (
                <VersterkenDialog
                  opInzet={false}
                  werklijnGegevens={dialogData as VersterkDialogData}
                  handleOpenChange={closeDialog}
                  showDialog={showDialog}
                />
              )}
              {dialogKey === 'inzet-versterken' && !(dialogData as VersterkDialogData).details?.isClaimUpdate && (
                <VersterkenDialog
                  opInzet={true}
                  werklijnGegevens={dialogData as VersterkDialogData}
                  handleOpenChange={closeDialog}
                  showDialog={showDialog}
                />
              )}
              {dialogKey === 'inzet-verzwakken' &&
                !((dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails)
                  .isClaimUpdate && (
                  <VerzwakDialog
                    showDialog={showDialog}
                    handleOpenChange={closeDialog}
                    details={
                      (dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails
                    }
                  />
                )}
              {dialogKey == 'multi-versterken' && (
                <MultiVersterkenDialog showDialog={showDialog} handleOpenChange={closeDialog} details={selectedItems} />
              )}
              {dialogKey == 'multi-verzwakken' && (
                <MultiVerzwakkenDialog showDialog={showDialog} handleOpenChange={closeDialog} details={selectedItems} />
              )}
              {dialogKey === 'accepteer-conflict' && (
                <AccepteerConflictDialog
                  showDialog={showDialog}
                  dialogData={(dialogData as unknown as any) || null}
                  handleOpenChange={closeDialog}
                  title='Accepteer conflicten'
                />
              )}
              {dialogKey === 'knippen' &&
                !((dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails)
                  ?.isClaimUpdate && (
                  <KnippenDialog
                    showDialog={showDialog}
                    handleOpenChange={closeDialog}
                    details={
                      (dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails
                    }
                  />
                )}
              {dialogKey == 'inregeling-wijzigen' && (
                <ToevoegenInregelenDialog
                  isUpdate
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={(dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails}
                />
              )}
              {dialogKey === 'verwijder-claim' && (
                <VerwijderClaimDialog
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={(dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails}
                />
              )}
            </>
          )}
          {dialogActionOptions === 'contextmenu' && (
            <>
              {['claim-toevoegen', 'claim-wijzigen'].includes(String(dialogKey)) && (
                <ClaimDialog
                  isUpdate={dialogKey === 'claim-wijzigen'}
                  showDialog={showDialog}
                  werklijnGegevens={dialogData as ClaimDialogData}
                  handleOpenChange={closeDialog}
                  dienstregelpunten={dienstregelpunten}
                />
              )}
              {dialogKey == 'inzet-wijzigen' && (
                <InzetWijzigenDialog
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={(dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails}
                />
              )}
              {dialogKey == 'niet-inzetbaar-vanaf' && (
                <MeldDefectDialog
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={(dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails}
                />
              )}
              {dialogKey == 'defect-gereedmelden' && (
                <DefectGereedMeldenDialog
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={(dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails}
                />
              )}
              {dialogKey == 'inregeling-verwijderen' && (
                <VerwijderenInregelenDialog
                  showDialog={showDialog}
                  handleOpenChange={closeDialog}
                  details={(dialogData as unknown as MwdContextMenuInformation)?.details as WerklijnContextMenuDetails}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
export default MwdActions;
